import React, { useEffect, useMemo } from "react";
import DOMPurify from "dompurify";

import _ from "lodash";
import cx from "classnames";

import styles from "./view.module.scss";
import Button from "src/components/button";
import { format } from "date-fns";
import { Dropdown } from "react-bootstrap";
import { BannerView } from "src/components/banner";
import { LearningRecordCourse, LearningRecordSimulation } from "src/types/coaching/learners";
import { SimulationContext } from "./context";
import { useTransition, animated } from "@react-spring/web";
import { CallSimulator } from "./simulator";
import { DailySimulator } from "./dailySimulator";
import { VapiContextProvider } from "./simulator/hook";
import { DeviceSetup } from "./deviceSetup";
import { FileService } from "src/utils/file.service";
import { CALL_TYPES, DIFFICULTY_CHOICES } from "src/constants/simulation";
import {
  ContentManageLink,
  useShowContentManageLink,
} from "src/pages/coaching/components/contentManageLink";
import Text from "src/components/text";
import Thumbnail from "src/components/thumbnail";
import { getDueDate } from "../../course/view";
import { useLocation } from "react-router-dom";

interface SimulationSummaryComponentProps {
  simulation: LearningRecordSimulation;
  parentCourse?: LearningRecordCourse;
  className?: string;
  isReadOnly?: boolean;
}

const languageOptions = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
  { label: 'German', value: 'de' },
  { label: 'French', value: 'fr' },
  { label: 'Marathi', value: 'mr' },
  { label: 'Polish', value: 'pl' }
];

export const SimulationSummaryComponent: React.FC<SimulationSummaryComponentProps> = ({ simulation, parentCourse, className, isReadOnly}) => {
  const simulationContext = React.useContext(SimulationContext);

  const summaryView = simulationContext.view === "summary";
  const deviceSetupView = simulationContext.view === "device_setup";

  const session = useMemo(() => _.get(simulation, "session"), [simulation]);

  const viewTransition = useTransition(summaryView, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const deviceSetupTransition = useTransition(deviceSetupView, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const callTransition = useTransition(!summaryView && !deviceSetupView, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleDeviceSetupConfirm = () => {
    const status = simulation.status;
    const report = simulation.report;
    if (status === "NOT_STARTED" && simulationContext.startSimulation) {
      simulationContext.startSimulation();
      return;
    }
    if (status === "IN_PROGRESS" && simulationContext.restartSimulation) {
      simulationContext.restartSimulation();
      return;
    }
    if (
      status === "COMPLETED" &&
      report?.status === "in_sufficient_duration" &&
      simulationContext.restartSimulation
    ) {
      simulationContext.restartSimulation();
      return;
    }
    if (status === "COMPLETED" && simulationContext.retakeSimulation) {
      simulationContext.retakeSimulation();
    }
  };

  const deviceSetupSubmitting = !!simulationContext.startingSimulation || !!simulationContext.restartingSimulation || !!simulationContext.retakingSimulation;

  const handleViewReport = (attemptId?: string) => {
    if (simulationContext.viewReport) {
      simulationContext.viewReport(attemptId);
    }
  }

  useEffect(() => {
    if (session && simulation?.status !== "COMPLETED") {
      simulationContext.changeView?.("device_setup");
    }
  }, [session, simulation?.status]);

  return (
    <div className={styles.simulationContainer}>
      {viewTransition(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.screen}>
              <SimulationView
                simulation={simulation}
                parentCourse={parentCourse}
                onViewReport={handleViewReport}
                className={className}
                isReadOnly={isReadOnly}
              />
            </animated.div>
          ),
      )}

      {deviceSetupTransition(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.screen}>
              <DeviceSetup
                simulation={simulation}
                onClose={() => simulationContext.changeView?.("summary")}
                onConfirmStart={handleDeviceSetupConfirm}
                isSubmitting={deviceSetupSubmitting}
                className={className}
              />
            </animated.div>
          ),
      )}

      {callTransition(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.screen}>
             {simulation?.simulation?.type === "elevator_pitch" && (
                <DailySimulator simulation={simulation} className={className} />
              )}
              {simulation?.simulation?.type !== "elevator_pitch" && (
                <VapiContextProvider>
                  <CallSimulator simulation={simulation} className={className} />
                </VapiContextProvider>
              )}
            </animated.div>
          ),
      )}
    </div>
  );
};

interface SimulationViewProps {
  simulation: LearningRecordSimulation;
  parentCourse?: LearningRecordCourse;
  onViewReport: (attemptId?: string) => void;
  className?: string;
  isReadOnly?: boolean;
}

const SimulationView: React.FC<SimulationViewProps> = ({
  simulation,
  parentCourse,
  onViewReport,
  className,
  isReadOnly,
}) => {

  const simulationContext = React.useContext(SimulationContext);
  const allowLearnerAttemptsDropdown = simulationContext.showLearnerAttemptsDropdown !== false;
  const location = useLocation();
  const showManageLink = useShowContentManageLink();
  const showSimulationDetails = location.pathname.includes("/courses/");
  const [showAttemptsDropdown, setShowAttemptsDropdown] = React.useState(false);

  const attempts = useMemo(() => _.get(simulation, "attempts", []) as any[], [simulation]);
  
  const attemptNumber = useMemo(
    () =>
      attempts
        .map((item, index, arr) => ({
          ...item,
          attemptNumber: arr.length - index,
        }))
        .filter((a) => {
          if ((a.score ?? 0) > 0) return true;
          const attemptReport =
            a?.id === simulation.id ? (a?.report ?? simulation.report) : a?.report;
          if (attemptReport?.status === "in_sufficient_duration") return false;
          if (attemptReport?.status === "failed") return true;
          return a?.status === "COMPLETED";
        }),
    [attempts, simulation.id, simulation.report],
  );

  const previousAttempts = useMemo(
    () => attemptNumber.filter((a) => a?.id && a.id !== simulation.id),
    [attemptNumber, simulation.id],
  );
  
  const name = useMemo(() => _.get(simulation, "simulation.name") || "", [simulation]);
  const status = useMemo(() => _.get(simulation, "status"), [simulation]);
  const description = useMemo(() => _.get(simulation, "simulation.description") || "", [simulation]);
  const banner = useMemo(() => _.get(simulation, "simulation.banner"), [simulation]);
  const type = useMemo(() => _.get(simulation, "simulation.type",null), [simulation])
  const createdBy = useMemo(() => _.get(simulation, "simulation.publishedBy.icon",null), [simulation])
  const firstName = useMemo(() => _.get(simulation, "simulation.publishedBy.firstName", null), [simulation])
  const lastName = useMemo(() => _.get(simulation, "simulation.publishedBy.lastName", null), [simulation])
  const dueDate = useMemo(()=> _.get(simulation, "dueDate",null), [simulation])
  const skills = useMemo(()=> _.get(simulation, "simulation.skills",[]),[simulation])
  const persona = useMemo(()=> _.get(simulation, "simulation.persona",null),[simulation])
  const simulationContentId = useMemo(() => {
    const catalogId = _.get(simulation, "simulation.id") as string | undefined;
    return catalogId || (_.get(simulation, "id") as string | undefined);
  }, [simulation]);
  const simulationPublishStatus = useMemo(
    () => _.get(simulation, "simulation.status") as string | undefined,
    [simulation],
  );

  const callType = useMemo(() => {
    const value = _.get(simulation, "simulation.type") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);

  const difficulty = useMemo(() => {
    const value = _.get(simulation, "simulation.difficulty") || "";
    return DIFFICULTY_CHOICES.find((item) => item.value === value)?.label || "";
  }, [simulation]);

  const language = useMemo(() => {
    const languageValue = _.get(simulation, "simulation.language");
  
    const option = languageOptions.find(option => option.value === languageValue);
  
    return option?.label || "English";
  }, [simulation]);

  const pitchConfiguration = simulation?.simulation?.type === "elevator_pitch";
  const report = simulation?.report;
  const insufficientDuration = report?.status === "in_sufficient_duration";
  const reportReady =
    report?.status === "completed" ||
    (report?.status === "failed" && report?.errorCode === 100002);
  const reportProcessing =
    !!report && !reportReady && !insufficientDuration;

  const handleBackClicked = () => {
    if (simulationContext.onBack) {
      simulationContext.onBack();
    }
  }

  const handleEnrollSimulation = () => {
    if (simulationContext.enrollSimulation) {
      simulationContext.enrollSimulation();
    }
  }

  const showDeviceSetup = () => {
    simulationContext.changeView?.("device_setup");
  };

  const handleRestartSimulation = () => {
    showDeviceSetup();
  };

  const handleViewReports = () => {
    onViewReport(simulation.id);
  }

  const handleAttemptSelect = (attemptId: string) => {
    setShowAttemptsDropdown(false);
    onViewReport(attemptId);
  };

  const handleRetakeSimulation = () => {
    showDeviceSetup();
  };

  return (
    <div className={cx(styles.simulationViewContainer, className)}>
      {showManageLink && simulationContentId && (
        <div className={styles.manageBar}>
          <ContentManageLink
            entityType="simulation"
            contentId={simulationContentId}
            status={simulationPublishStatus}
          />
        </div>
      )}
      <div className={cx(styles.left, {[styles.expanded]:showSimulationDetails})}>
        <div className={cx(styles.simulationDetails, {[styles.simulationDetailsinCourse]: showSimulationDetails})}>
          {simulationContext.onBack && (
            <div className={styles.backButton} onClick={handleBackClicked}>
              <i className="paperflite-left-arrow"></i>
              <div>
                <span className={styles.back}>back</span>{" "}
              </div>
            </div>
          )}

          <div className={styles.details}>
            <div className={styles.header}>
              <div
                className={cx(
                  styles.thumbnail,
                  (type === "discovery" && styles.discovery) ||
                    (type === "cold_call" && styles.coldcall) ||
                    (type === "elevator_pitch" && styles.elevatorPitch)
                )}
              >
                <BannerView
                  banner={banner as any}
                  defaultBanner="/images/assets/simulationThumbnail.jpg"
                  overlay={false}
                />
              </div>
              <div className={styles.right}>
                <Text className={styles.name} text={name} lines={2} />
                <div className={styles.actions}>
                  {!isReadOnly && status === "NOT_ENROLLED" && !parentCourse && (
                    <Button
                      className={styles.startCourse}
                      text={
                        simulationContext.enrollingSimulation
                          ? "Enrolling..."
                          : "Enroll Call"
                      }
                      variant="courses-primary"
                      icon="paperflite-phone"
                      disabled={simulationContext.enrollingSimulation}
                      onClick={handleEnrollSimulation}
                    />
                  )}

                  {!isReadOnly && status === "NOT_ENROLLED" && parentCourse && (
                    <Button
                      className={styles.startCourse}
                      text={simulationContext.enrollingSimulation ? "Starting..." : "Start Simulation"}
                      icon="paperflite-phone"
                      variant="courses-primary"
                      disabled={simulationContext.enrollingSimulation}
                      onClick={handleEnrollSimulation}
                    />
                  )}

                  {!isReadOnly && (status === "NOT_STARTED" || status === "IN_PROGRESS") && (
                    <Button
                      className={styles.startCourse}
                      text={
                        simulationContext.startingSimulation
                          ? "Starting..."
                          : `Start Call ${getDueDate(dueDate!)}`
                      }
                      variant="courses-primary"
                      icon="paperflite-phone"
                      disabled={simulationContext.startingSimulation}
                      onClick={showDeviceSetup}
                    />
                  )}

                  {status === "COMPLETED" && !insufficientDuration && (
                      <>
                        {reportReady &&
                          (attemptNumber.length === 1 ||
                            (!allowLearnerAttemptsDropdown && attemptNumber.length > 1)) && (
                            <Button
                              className={styles.viewReports}
                              text={
                                simulationContext.fetchingReport
                                  ? "Fetching report..."
                                  : "View Reports"
                              }
                              variant="courses-primary"
                              icon="paperflite-analytics"
                              disabled={
                                simulationContext.fetchingReport ||
                                simulationContext.retakingSimulation
                              }
                              onClick={handleViewReports}
                            />
                          )}
                        
                        {allowLearnerAttemptsDropdown &&
                          reportReady &&
                          attemptNumber.length > 1 && (
                          <AttemptsDropdown
                            attempts={attemptNumber}
                            show={showAttemptsDropdown}
                            onToggle={(open) => setShowAttemptsDropdown(open)}
                            fetchingReport={simulationContext.fetchingReport}
                            retakingSimulation={simulationContext.retakingSimulation}
                            onSelect={handleAttemptSelect}
                            label="View Reports"
                          />
                        )}

                        {reportProcessing && (
                            <>
                              {allowLearnerAttemptsDropdown && previousAttempts.length > 0 && (
                                <AttemptsDropdown
                                  attempts={previousAttempts}
                                  show={showAttemptsDropdown}
                                  onToggle={(open) => setShowAttemptsDropdown(open)}
                                  fetchingReport={simulationContext.fetchingReport}
                                  retakingSimulation={simulationContext.retakingSimulation}
                                  onSelect={handleAttemptSelect}
                                  label="Processing call..."
                                />
                              )}

                              {((!allowLearnerAttemptsDropdown && previousAttempts.length > 0) ||
                                previousAttempts.length === 0) && (
                                <Button
                                  className={styles.proceesingReports}
                                  text={"Processing call..."}
                                  variant="courses-primary"
                                  icon="paperflite-analytics"
                                />
                              )}
                            </>
                          )}
                      </>
                    )}

                  {!isReadOnly && status === "COMPLETED" &&
                    insufficientDuration && (
                      <Button
                        className={styles.viewReports}
                        text={
                          simulationContext.restartingSimulation
                            ? "Retaking..."
                            : "Retake Simulation"
                        }
                        variant="courses-primary"
                        icon="paperflite-reverse"
                        disabled={simulationContext.restartingSimulation}
                        onClick={handleRestartSimulation}
                      />
                    )}

                  {!isReadOnly && status === "COMPLETED" &&
                    !insufficientDuration && (
                      <Button
                        className={cx(styles.startCourse, styles.retake)}
                        text={
                          simulationContext.retakingSimulation
                            ? "Retaking..."
                            : "Retake Simulation"
                        }
                        variant="secondary"
                        icon="paperflite-reverse"
                        disabled={simulationContext.retakingSimulation}
                        onClick={handleRetakeSimulation}
                      />
                    )}
                </div>
              </div>
            </div>

            <div className={styles.simulationMetadata}>
              {!pitchConfiguration && (
                <div className={styles.metadata}>
                  <i className="paperflite-clock" />
                  {`${simulation.simulation.duration} Minutes`}
                </div>
              )}
              {pitchConfiguration && (
                <div className={styles.metadata}>
                  <i className="paperflite-clock" />
                  {FileService.formatTimeBound(simulation?.simulation?.timeBound ?? 0, 'short')}
                </div>
              )}
              <div className={styles.metadata}>
                <i className="paperflite-calendar" />
                {callType}
              </div>
              {!pitchConfiguration && (
                <>
                  <div className={styles.metadata}>
                    <i className="paperflite-speedomenter" />
                    {difficulty}
                  </div>
                  <div className={styles.metadata}>
                    <i className="paperflite-language" />
                    {language}
                  </div>
                </>
              )}
              {!parentCourse && (
                 <div className={cx(styles.metadata,styles.profile)}>
                  <Thumbnail
                    media={createdBy}
                    defaultImage="/images/assets/default_asset.jpg"
                    className={styles.icon}
                  />
                  by {firstName} {lastName}
                </div>
              )}
            </div>

            {!pitchConfiguration && (
              <>
                <div className={styles.description}>
                  <p className={styles.title}>Description</p>
                  <div
                    className={styles.descriptionContent}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(description),
                    }} />
                </div>
                <div className={styles.cards}>
                  <div className={styles.organizationCard}>
                    <div className={styles.header}>
                      <div className={styles.icon}>
                        <i className="paperflite-buildings" />
                      </div>
                      <div className={styles.name}>
                        {simulation.simulation?.organization?.name || "no name"}
                      </div>
                    </div>
                    <div className={styles.description}>
                      {simulation.simulation?.organization?.description || ""}
                    </div>
                  </div>
                  <div className={styles.personaCard}>
                    <div className={styles.header}>
                      {persona?.icon && (
                        <Thumbnail
                          media={persona?.icon}
                          defaultImage="/images/assets/simulationThumbnail.jpg"
                          className={cx(styles.icon, styles.image)} />
                      )}

                      {!persona?.icon && (
                        <i
                          className={cx(styles.icon, "paperflite-user-settings")} />
                      )}

                      <div className={styles.name}>
                        {simulation.simulation?.persona?.name || "no name"}
                      </div>
                    </div>
                    <div className={styles.description}>
                      {simulation.simulation?.persona?.description || ""}
                    </div>
                  </div>
                  <div className={styles.objectivesCard}>
                    <div className={styles.header}>
                      <div className={styles.icon}>
                        <i className="paperflite-objectives" />
                      </div>
                      <div className={styles.name}>Objectives</div>
                    </div>
                    <div className={styles.description}>
                      {simulation.simulation?.objectives?.description || ""}
                    </div>
                  </div>
                </div>
              </>
            )}
            {pitchConfiguration && (
              <>
                <div className={styles.cards}>
                  <div className={styles.objectivesCard}>
                    <div className={styles.header}>
                      <i
                        className={cx(styles.icon, "paperflite-objectives")} />
                      <div className={styles.name}>
                        Pitch Scenario
                      </div>

                    </div>
                    <div className={styles.description}>
                      {simulation.simulation?.pitchScenario || "no scenario provided"}
                    </div>
                  </div>
                  <div className={styles.personaCard}>
                    <div className={styles.header}>
                      <i
                        className={cx(styles.icon, "paperflite-user-settings")} />
                      <div className={styles.name}>
                        Pitch Audience
                      </div>
                    </div>
                    <div className={styles.description}>
                      {simulation.simulation?.pitchAudience || "no audience provided"}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={cx(styles.right, { [styles.hide]: showSimulationDetails })}>
        <div className={styles.wrapper}>
          {status === "COMPLETED" && (simulation?.score ?? 0) > 0 && (
            <div className={styles.infoCard}>
              <h6 className={styles.name}>latest score</h6>
              <div className={styles.score}>
                {Math.min(
                  Number(((simulation.score || 0) * 100).toFixed(2)),
                  100,
                )}%
              </div>
            </div>
          )}
          <div className={styles.skillsWrapper}>
            <div className={styles.heading}>
              skills you {status === "COMPLETED" ? "have earned" : "will earn"}
            </div>
            <div className={styles.skills}>
              {skills.map((skill, idx) => {
                return <div className={styles.skill}>{skill?.name}</div>;
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
};

interface AttemptsDropdownProps {
  attempts: any[];
  show: boolean;
  onToggle: (open: boolean) => void;
  fetchingReport?: boolean;
  retakingSimulation?: boolean;
  onSelect: (attemptId: string) => void;
  label: string;
}

const AttemptsDropdown: React.FC<AttemptsDropdownProps> = ({
  attempts,
  show,
  onToggle,
  fetchingReport,
  retakingSimulation,
  onSelect,
  label,
}) => {
  return (
    <div className={styles.learnerReportDropdown}>
      <Dropdown show={show} onToggle={(open) => onToggle(open)}>
        <Dropdown.Toggle
          as="button"
          disabled={fetchingReport || retakingSimulation}
        >
          <div className={styles.viewReportsToggle}>
            <i className="paperflite-analytics" />
            <span>{fetchingReport ? "Fetching report..." : label}</span>
          </div>
          <i className={cx("paperflite-dropdown", { [styles.showDropdown]: show })} />
        </Dropdown.Toggle>
        <Dropdown.Menu
          renderOnMount
          popperConfig={{
            strategy: "fixed",
            modifiers: [
              { name: "offset", options: { offset: [0, 6] } },
              { name: "flip", options: { fallbackPlacements: [] } },
            ],
          }}
          >
          {attempts.map((attempt) => {
            const ts = attempt?.modifiedDate ?? attempt?.createdDate ?? 0;
            const formattedDateTime = format(new Date(ts).toISOString(), "do MMM hh:mm a");
            return (
              <Dropdown.Item
                key={attempt.id}
                className={styles.learnerReportDropdownItem}
                onClick={() => onSelect(attempt.id)}
              >
                <span>
                  {String(attempt.attemptNumber).padStart(2, "0")} /{" "}
                  {Math.round((attempt.score ?? 0) * 100)} % /{" "}
                  {formattedDateTime}
                </span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};